<script setup>
/**
 * The Customization drawer.
 *
 * Every colour control says whether the colour can actually be seen, and keeps
 * the WCAG ratio in its tooltip. That is not decoration: a colour that fails
 * here is a line nobody can read, and the drawer is where that becomes
 * someone's fault rather than a surprise three weeks later.
 * docs/CHARTS.md "Contrast is reported, not assumed".
 *
 * EVERY SECTION IS SCOPED TO A PAGE. A control that changes nothing you can see
 * is the fastest way to make a preferences panel feel broken, so the table
 * below says which sections each page actually draws with, and the drawer shows
 * only those. The per-hotel series colours are not here at all: no page in this
 * app draws with them. They belong to the engineering page, which has its own
 * Customize drawer and writes the same stored theme.
 *
 * WHICH COMPETITORS APPEAR IS NOT A PREFERENCE. This drawer used to let a
 * reader add a website by URL and drop any of the others. That is the wrong
 * shape of control for a page shown to a hotel as a comparison against the
 * market: a comparison whose membership the presenter can edit in front of the
 * audience is not evidence. The sample is decided in `config/competitors.json`
 * and reviewed there. docs/BENCHMARKS.md.
 *
 * A change is saved the moment it is made, and closing by any route — ✕,
 * Done, scrim, Escape — keeps it. There was a Save button, and closing without
 * pressing it threw the changes away, which read as the drawer forgetting.
 * A short "Saved" confirms each change; a browser that blocks site data gets a
 * warning that stays, since the change will be gone on the next visit.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from '../../composables/useTheme.js'
import { checkContrast } from '../../lib/colour.js'
import { offers } from '../../lib/customise.js'
import { t, tk } from '../../lib/i18n.js'
import Phrase from './Phrase.vue'

const emit = defineEmits(['close'])
const theme = useTheme()
const route = useRoute()

/**
 * Which sections each page draws with: lib/customise.js, the one table the
 * header and the Overview read too. Diagnostics alone offers the series
 * palette, because it is the one page that draws a line per hotel. No page
 * offers the highlight colour any more (decision 0025); its section stays for
 * the day one does.
 */
const shows = (name) => offers(route.path, name)

const savedNote = ref('')
let noteTimer = null

/** Say what happened to the change just made. */
function said(ok) {
  clearTimeout(noteTimer)
  savedNote.value = ok ? t('Saved') : t('Could not save. This browser is blocking site data.')
  if (ok) noteTimer = setTimeout(() => (savedNote.value = ''), 1600)
}

const setMode = (mode) => said(theme.setMode(mode))
const setColour = (path, value) => said(theme.setColour(path, value))
function resetAll() {
  theme.reset()
  said(true)
}

function close() {
  emit('close')
}

function onKey(e) {
  if (e.key === 'Escape') close()
}
// The page behind a modal drawer does not scroll. Without this a wheel over
// the scrim moved the page underneath, and the chart a colour was being chosen
// for slid out of view. The previous value is restored, not blanked.
let overflow = ''
onMounted(() => {
  document.addEventListener('keydown', onKey)
  overflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
})
onBeforeUnmount(() => {
  clearTimeout(noteTimer)
  document.removeEventListener('keydown', onKey)
  document.body.style.overflow = overflow
})

const surface = computed(() => theme.css('--surface') || '#ffffff')

/** The accent is used as text AND as a fill behind text, so it is held to the
 *  text ratio on the harder of the two. */
const accentCheck = computed(() => checkContrast(theme.accent.value, surface.value, { text: true }))

/** Mobile and desktop: every score chart on every page draws these two. */
const rows = computed(() => [
  { id: 'device.mobile', label: t('Mobile line'), value: theme.device.value.mobile[0] },
  { id: 'device.desktop', label: t('Desktop line'), value: theme.device.value.desktop[0] },
])

/** The hotel colours, in the order the customization charts hand them out. */
const series = computed(() =>
  theme.palette.value.map((pair, i) => ({
    id: `hotel.${i}`,
    label: t('Hotel {n}', { n: i + 1 }),
    value: pair[0],
  })),
)

/** Chart marks are graphical objects: 3:1 against the card they sit on. */
/** The theme modes' names, in the language on screen. */
const MODE_WORDS = { system: tk('System'), light: tk('Light'), dark: tk('Dark') }
const modeWord = (m) => t(MODE_WORDS[m] ?? m)

const checkOf = (colour) => checkContrast(colour, surface.value)
</script>

<template>
  <div class="scrim" @click="close"></div>
  <aside class="drawer" role="dialog" aria-modal="true" :aria-label="t('Customise')">
    <div class="dw-head">
      <h3>{{ t('Customise') }}</h3>
      <button type="button" :aria-label="t('Close')" @click="close">✕</button>
    </div>

    <div class="dw-body">
      <section v-if="shows('appearance')" class="dw-sec">
        <h4>{{ t('Appearance') }}</h4>
        <div class="seg">
          <button
            v-for="m in theme.MODES"
            :key="m"
            type="button"
            :class="{ on: theme.theme.mode === m }"
            @click="setMode(m)"
          >
            {{ modeWord(m) }}
          </button>
        </div>
        <p class="dw-note">{{ t('A preference, not a fact about the data.') }}</p>
      </section>

      <section v-if="shows('accent')" class="dw-sec">
        <h4>{{ t('Highlight colour') }}</h4>
        <div class="crow">
          <label for="c-accent">{{ t('Buttons and highlights') }}</label>
          <span
            class="wcag"
            :class="accentCheck.pass ? 'pass' : 'fail'"
            :title="accentCheck.detail"
            >{{ accentCheck.words }}</span
          >
          <input
            id="c-accent"
            type="color"
            :value="theme.accent.value"
            :aria-describedby="'accent-note'"
            @input="setColour('accent', $event.target.value)"
          />
        </div>
        <p id="accent-note" class="dw-note">
          {{
            t(
              'This one is used for text as well as fills, so it is held to the stricter of the two.',
            )
          }}
        </p>
      </section>

      <section v-if="shows('lines')" class="dw-sec">
        <h4>{{ t('Chart lines') }}</h4>
        <div v-for="row in rows" :key="row.id" class="crow">
          <label :for="`c-${row.id}`">{{ row.label }}</label>
          <span
            class="wcag"
            :class="checkOf(row.value).pass ? 'pass' : 'fail'"
            :title="checkOf(row.value).detail"
            >{{ checkOf(row.value).words }}</span
          >
          <input
            :id="`c-${row.id}`"
            type="color"
            :value="row.value"
            @input="setColour(row.id, $event.target.value)"
          />
        </div>
        <p class="dw-note">
          {{ t('A line only has to stand out from the card behind it, not be readable as text.') }}
        </p>
      </section>

      <section v-if="shows('series')" class="dw-sec">
        <h4>{{ t('Hotel colours') }}</h4>
        <div v-for="row in series" :key="row.id" class="crow">
          <label :for="`c-${row.id}`">{{ row.label }}</label>
          <span
            class="wcag"
            :class="checkOf(row.value).pass ? 'pass' : 'fail'"
            :title="checkOf(row.value).detail"
            >{{ checkOf(row.value).words }}</span
          >
          <input
            :id="`c-${row.id}`"
            type="color"
            :value="row.value"
            @input="setColour(row.id, $event.target.value)"
          />
        </div>
        <p class="dw-note">
          {{
            t(
              'Handed out in this order to the hotels in each customization chart, so the same hotel keeps its colour from one chart to the next.',
            )
          }}
        </p>
      </section>
    </div>

    <!-- Said once, by the footer, because changing something is the moment a
         reader forms an expectation about who else will see it. -->
    <p class="dw-where">
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="3" y="4" width="18" height="13" rx="2" />
        <path d="M8 20.5h8" />
        <path d="M12 17v3.5" />
      </svg>
      <Phrase
        tag="span"
        k="Everything here is saved on {where}. It changes how the dashboard looks for you, never the measurements, and never what anyone else sees."
      >
        <template #where>
          <b>{{ t('this device only') }}</b>
        </template>
      </Phrase>
    </p>

    <div class="dw-foot">
      <button type="button" class="reset" @click="resetAll">
        {{ t('Reset to defaults') }}
      </button>
      <span class="dw-saved" role="status">{{ savedNote }}</span>
      <button type="button" class="primary" @click="close">{{ t('Done') }}</button>
    </div>
  </aside>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgb(0 0 0 / 0.45);
  animation: fade var(--mid) var(--ease);
}

@keyframes fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes slidein {
  from {
    transform: translateX(100%);
  }
  to {
    transform: none;
  }
}

.drawer {
  position: fixed;
  z-index: 61;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(400px, 92vw);
  background: var(--surface);
  border-left: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 36px rgb(0 0 0 / 0.28);
  animation: slidein var(--slow) var(--ease);
}

.dw-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
}

.dw-head h3 {
  margin: 0;
  flex: 1 1 auto;
  font-size: 15px;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink);
}

.dw-head h3::before {
  content: none;
}
.dw-head button {
  border: none;
  background: none;
  color: var(--ink3);
  padding: 4px 8px;
}
.dw-head button:hover {
  color: var(--ink);
}

.dw-body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 14px 16px 18px;
}
.dw-sec {
  margin: 0 0 18px;
}

.dw-sec > h4 {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--ink3);
  font-weight: 600;
}

.seg {
  display: flex;
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  overflow: hidden;
  width: 100%;
}

.seg button {
  flex: 1 1 0;
  border: none;
  border-radius: 0;
  background: none;
  color: var(--ink3);
  font-size: 13px;
  padding: 7px 0;
}

.seg button + button {
  border-left: 1px solid var(--line);
}
.seg button.on {
  background: var(--accent);
  color: var(--on-accent, #fff);
  font-weight: 600;
}

/* One row per colour: the swatch is the input, so the control and the thing it
   controls are the same object. */
.crow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
}
.crow label {
  flex: 1 1 auto;
  font-size: 13px;
  color: var(--ink2);
}

.crow input[type='color'] {
  width: 34px;
  height: 26px;
  padding: 2px;
  border-radius: 6px;
  border: 1px solid var(--line);
  background: var(--surface);
  cursor: pointer;
}

/* The verdict in words. The ratio is in the `title`, for whoever wants it. */
.wcag {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 999px;
  white-space: nowrap;
  text-align: center;
  cursor: help;
}

.wcag.pass {
  background: color-mix(in srgb, var(--good) 16%, transparent);
  color: var(--good);
}
.wcag.fail {
  background: color-mix(in srgb, var(--bad) 14%, transparent);
  color: var(--bad);
}

.dw-note {
  font-size: 12px;
  color: var(--ink3);
  margin: 6px 0 0;
}
.dw-note.top {
  margin: 0 0 8px;
}

.sites {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 3px;
}

.site {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px 7px 10px;
  border-radius: var(--r-sm);
  font-size: 13.5px;
  color: var(--ink);
}

.site:hover {
  background: color-mix(in srgb, var(--ink) 7%, transparent);
}

/* Removed, but still listed so it can be put back. Struck through rather than
   hidden: a list that silently shrinks gives the reader no way to undo. */
.site.off {
  color: var(--ink3);
}
.site.off .nm {
  text-decoration: line-through;
}

.nm {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nm em {
  font-style: normal;
  font-size: 10.5px;
  color: var(--ink3);
  margin-left: 7px;
  white-space: nowrap;
}

.act {
  flex: 0 0 auto;
  border: none;
  background: none;
  color: var(--ink3);
  font: inherit;
  font-size: 12px;
  padding: 3px 7px;
  border-radius: 6px;
}

.act:hover {
  color: var(--ink);
  background: color-mix(in srgb, var(--ink) 10%, transparent);
}
.act.x {
  font-size: 13px;
  line-height: 1;
}
.act.x:hover {
  color: var(--bad);
  background: color-mix(in srgb, var(--bad) 14%, transparent);
}

.addrow {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}

.addrow input {
  flex: 1 1 auto;
  min-width: 0;
  font: inherit;
  font-size: 13px;
  padding: 7px 10px;
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  background: transparent;
  color: var(--ink);
}

.addbtn {
  flex: 0 0 auto;
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--on-accent, #fff);
  font-size: 12.5px;
  font-weight: 650;
  padding: 7px 14px;
  border-radius: var(--r-sm);
}

.addbtn:disabled {
  opacity: 0.45;
  cursor: default;
}

.dw-note.bad {
  color: var(--bad);
}

.showall {
  margin-top: 8px;
  background: none;
  border: none;
  color: var(--accent);
  font-size: 12.5px;
  padding: 4px 0;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.dw-where {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 0;
  padding: 11px 16px;
  border-top: 1px solid var(--line);
  font-size: 12px;
  line-height: 1.5;
  color: var(--ink3);
  background: color-mix(in srgb, var(--ink) 3%, transparent);
}

.dw-where svg {
  flex: 0 0 auto;
  width: 15px;
  height: 15px;
  margin-top: 1px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
}

.dw-where b {
  color: var(--ink2);
  font-weight: 650;
}

.dw-foot {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid var(--line);
}

.dw-foot button {
  font-size: 13px;
}
.reset {
  border: none;
  background: none;
  color: var(--ink3);
  margin-right: auto;
  padding: 6px 0;
}
.reset:hover {
  color: var(--ink);
}
.dw-saved {
  font-size: 12px;
  color: var(--ink3);
}

.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent, #fff);
  font-weight: 600;
}

.primary:hover {
  filter: brightness(1.08);
}
</style>
